import {
	NavigationIntent,
	OrganizationType,
	routingClient
} from '@newo/routing-client';
import {
	CreatorAgentSuccessFlowStepKey as AgentSuccessStep,
	CreatorAgentSuccessRedirectTarget
} from '@newo/tenant-metadata';
import { type MouseEvent, useCallback, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { runtimeConfig } from '@/constants';
import { useHostContext } from '@/context/HostContext.tsx';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import { partnerMetadata } from '@/services/PartnerMetadata';
import { useAgentTraining } from '@/store/agent-training.ts';
import useAppStore from '@/store/app.ts';
import {
	AgentSuccessAction,
	type RedirectAction,
	type TAgentSuccessStepCard
} from '@/types/partner-metadata.types';
import { useActivateAccount } from '@/utils/api';
import { emailsStringToArray } from '@/utils/emails.ts';

export const useAgentSuccessActions = (
	config = partnerMetadata.getAgentSuccessConfig()
) => {
	const firstStep = config.stepOrder[0] ?? AgentSuccessStep.ActivateAccount;
	const [expandedStep, setExpandedStep] = useState<AgentSuccessStep>(firstStep);
	const [isRedirecting, setIsRedirecting] = useState(false);

	const { handleCloseCreator } = useHostContext();
	const [stepperActiveStep, setStepperActiveStep] = useState(0);
	const resetAgentTrainingState = useAgentTraining((state) => state.resetState);
	const {
		agentSuccessActiveStep,
		setAgentSuccessStep,
		agentData,
		customerData,
		resetState,
		setIsRedirectingToPortal
	} = useAppStore(
		useShallow((state) => ({
			agentData: state.agentData,
			customerData: state.customerData,
			agentSuccessActiveStep: state.agentSuccessActiveStep,
			setAgentSuccessStep: state.setAgentSuccessStep,
			resetState: state.resetState,
			setIsRedirectingToPortal: state.setIsRedirectingToPortal
		}))
	);
	const { mutate: sendActivateAccount } = useActivateAccount();

	const startAccountActivation = useCallback(() => {
		if (!agentData?.created_customer_idn) {
			return;
		}

		sendActivateAccount(
			{
				customerIdn: agentData.created_customer_idn,
				productKey: partnerMetadata.getCheckoutProductKey(),
				promotionCode: partnerMetadata.getCheckoutPromotionCode()
			},
			{
				onSuccess: (url) => {
					globalThis.location.href = url;
				}
			}
		);
	}, [agentData, sendActivateAccount]);

	const handleGenericRedirect = (url?: string) => {
		if (!url) {
			return;
		}
		window.open(url, '_blank');
	};

	const handleRedirectAction = (
		currentCard: TAgentSuccessStepCard & RedirectAction,
		cardKey: AgentSuccessStep
	) => {
		if (cardKey !== AgentSuccessStep.AccessDashboard) {
			handleGenericRedirect(currentCard.url);
			return;
		}

		if (!agentData?.created_customer_idn) {
			return;
		}

		setIsRedirecting(true);
		setIsRedirectingToPortal(true);

		const isAgentConfigurationTarget =
			currentCard.portalRedirectTarget ===
			CreatorAgentSuccessRedirectTarget.AgentConfiguration;
		const [primaryEmail] = emailsStringToArray(customerData?.contact_email);
		const isLoginHintIncluded =
			!runtimeConfig.isRemoteBuild &&
			partnerMetadata.isPortalLoginHintEnabled() &&
			Boolean(primaryEmail);

		routingClient.emitNavigation({
			intent: isAgentConfigurationTarget
				? NavigationIntent.CustomerAgentConfiguration
				: NavigationIntent.CustomerDashboard,
			params: {
				organizationIdn: agentData.created_customer_idn,
				organizationType: OrganizationType.Customer
			},
			query: {
				...(currentCard.onboardingTour && {
					onboarding_tour: currentCard.onboardingTour
				}),
				disableMiniCreator: '1',
				...(isLoginHintIncluded && { login_hint: primaryEmail })
			}
		});

		handleCloseCreator?.();
		agentTrainingRealtime.disconnect();
		resetState();
		resetAgentTrainingState();
	};

	const handleStepClick = useCallback(
		(val: number) => {
			const currentStep = config.stepOrder[val];

			if (!currentStep) {
				return;
			}

			setExpandedStep(currentStep);
			setStepperActiveStep(val);
		},
		[config.stepOrder]
	);

	const handleExpand = useCallback(
		(key: AgentSuccessStep) => {
			setExpandedStep(key);
			setStepperActiveStep(
				config.stepOrder.findIndex((value) => value === key)
			);
		},
		[config.stepOrder]
	);

	const handleActionClick = (ev: MouseEvent<HTMLButtonElement>) => {
		if (!agentSuccessActiveStep || !agentData) {
			return;
		}

		const cardKey = ev.currentTarget.dataset.cardKey as AgentSuccessStep;
		const currentCard = config.steps[cardKey];

		if (!currentCard) {
			return;
		}

		switch (currentCard.action) {
			case AgentSuccessAction.NEXT_CARD: {
				if (currentCard.nextStep) {
					handleExpand(currentCard.nextStep);
				}
				break;
			}
			case AgentSuccessAction.REDIRECT: {
				handleRedirectAction(currentCard, cardKey);
				if (currentCard.nextStep) {
					setAgentSuccessStep(currentCard.nextStep);
				}
				break;
			}
			case AgentSuccessAction.ACTIVATE_ACCOUNT: {
				startAccountActivation();
				break;
			}
			default: {
				break;
			}
		}
	};

	return {
		handleActionClick,
		stepperActiveStep,
		expandedStep,
		handleExpand,
		handleStepClick,
		startAccountActivation,
		isRedirecting
	};
};
